import { PlayerAnalytics } from '@eyevinn/player-analytics-client-sdk-web';
import {
  AD_EVENT_STATE_MAP,
  DEFAULT_HEARTBEAT_INTERVAL,
  RNPlayerEvents,
} from './constants';
import type {
  AdEventPayload,
  BitmovinRNAnalyticsConfig,
  BufferedArg,
  BufferingArg,
  ErrorArg,
  PausedArg,
  PlayerEventData,
  PlayingArg,
  SeekedArg,
  SeekingArg,
  StoppedArg,
} from './types';

export type { BitmovinRNAnalyticsConfig };

export const BitmovinRNAnalyticsIntegration = (
  config: BitmovinRNAnalyticsConfig
) => {
  // Note: We use PlayerAnalytics directly to avoid duplicate init sessions.
  // The metadata/ad/bitrate events are reported through this single reporter.

  let eyevinnPlayerAnalytics: PlayerAnalytics | null = null;
  let heartbeatInterval: NodeJS.Timeout | null = null;
  let completedAdsCount = 0;
  let adRequestCount = 0;
  let adMidQuartile = '0';
  let adThirdQuartile = '0';
  // Buffer events until init completes
  let isInitiated = false;
  const pendingEventQueue: (() => Promise<void> | void)[] = [];

  const enqueueOrRun = (fn: () => Promise<void> | void): void => {
    if (isInitiated && eyevinnPlayerAnalytics) {
      try {
        const maybePromise = fn();
        if (maybePromise && typeof maybePromise.then === 'function') {
          void maybePromise.catch(err => {
            if (config.debug) {
              console.warn('[BitmovinAnalytics] event dispatch failed', err);
            }
          });
        }
      } catch (err) {
        if (config.debug) {
          console.warn('[BitmovinAnalytics] event dispatch threw', err);
        }
      }
      return;
    }
    pendingEventQueue.push(fn);
  };

  const flushQueue = async (): Promise<void> => {
    // Drain in order
    while (pendingEventQueue.length) {
      const next = pendingEventQueue.shift();
      try {
        // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
        await next?.();
      } catch (err) {
        if (config.debug) {
          console.warn('[BitmovinAnalytics] queued event failed', err);
        }
      }
    }
  };

  const init = async () => {
    // Initialize the Eyevinn PlayerAnalytics for RN manual event reporting
    eyevinnPlayerAnalytics = new PlayerAnalytics(
      config.eventsinkUrl,
      config.debug
    );
    await eyevinnPlayerAnalytics.initiateAnalyticsReporter({
      sessionId: config.sessionId,
      heartbeatInterval: config.heartbeatInterval,
      shardId: config.shardId,
    });
    isInitiated = true;
    await reportInitialMetadata();
    await flushQueue();
    startHeartbeat();
  };

  const getPlayerState = async (): Promise<{
    muted: string;
    autoplay: string;
  }> => {
    let muted = '0';
    let autoplay = '0';
    if (config.player) {
      try {
        if (config.player.isMuted) {
          const isMuted = await config.player.isMuted();
          muted = isMuted ? '1' : '0';
        }
      } catch (error) {
        // Silently ignore errors when getting muted state
        console.warn('Failed to get muted state:', error);
      }

      try {
        const autoplayValue = config.player.autoplay?.();
        autoplay = autoplayValue ? '1' : '0';
      } catch (error) {
        // Silently ignore errors when getting autoplay state
        console.warn('Failed to get autoplay state:', error);
      }
    }

    return { muted, autoplay };
  };

  const reportInitialMetadata = async () => {
    if (!eyevinnPlayerAnalytics) return;
    const baseFields = await getBaseEventFields();
    const playerState = await getPlayerState();
    eyevinnPlayerAnalytics.metadata({
      event: 'metadata',
      ...baseFields,
      payload: {
        live: config?.playback?.isLive ?? false,
        contentTitle: config?.playback?.assetTitle ?? '',
        contentId: config?.playback?.assetId ?? '',
        contentUrl: config?.playback?.recommendedStream?.manifest?.uri ?? '',
        drmType: config?.playback?.isDrmProtected ?? 'none',
        deviceType: 'TvOS',
        userId: config.videoKey ?? '',
        categoryId: config?.playback.categoryId,
        deviceId: config?.deviceInfo?.deviceName ?? '',
        deviceModel: config?.deviceInfo?.model ?? '',
        playerProfileId: config.playerProfileId ?? '',
        playerSource: 'TvOS',
        domain: config.domain ?? '',
        muted: playerState.muted,
        autoplay: playerState.autoplay,
        ...(config.additionalMetadata ?? {}),
      },
    } as Parameters<PlayerAnalytics['metadata']>[0]);
  };
  const getBaseEventFields = async () => {
    const duration = config.duration ?? 0;
    let playhead: unknown = -1;

    // Get real-time current time from player
    if (config.player?.getCurrentTime) {
      try {
        playhead = await config.player.getCurrentTime();
      } catch (error) {
        playhead = -1;
        console.log('error', error);
      }
    }

    const playheadNumber =
      typeof playhead === 'number' && Number.isFinite(playhead) ? playhead : 0;

    const roundedPlayhead =
      Math.round((playheadNumber + Number.EPSILON) * 1000) / 1000;

    return {
      timestamp: Date.now(),
      playhead: roundedPlayhead,
      duration,
      sessionId: config.sessionId,
    };
  };

  const startHeartbeat = () => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
    }

    const interval = config.heartbeatInterval ?? DEFAULT_HEARTBEAT_INTERVAL;
    heartbeatInterval = setInterval(async () => {
      if (eyevinnPlayerAnalytics) {
        const baseFields = await getBaseEventFields();
        eyevinnPlayerAnalytics.heartbeat({
          event: 'heartbeat',
          ...baseFields,
        } as Parameters<PlayerAnalytics['heartbeat']>[0]);
      }
    }, interval);
  };

  const stopHeartbeat = () => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
  };

  // Build Eyevinn ad metadata payload, aligned with web shape
  const buildAdMetadataPayload = async (args: {
    eventState: string;
    event?: Record<string, unknown>;
  }) => {
    // Extract ad data from Bitmovin event structure
    const evt = args.event ?? {};
    const ad = (evt?.ad ?? {}) as Record<string, unknown>;
    const adBreak = (evt?.adBreak ?? {}) as Record<string, unknown>;
    const playerState = await getPlayerState();

    const adPosition = (evt?.position as 'pre' | 'mid' | 'post') ?? 'pre';
    const adDuration =
      typeof evt?.duration === 'number' ? String(evt.duration * 1000) : '';

    // For adAllCompleted (onAdBreakFinished), use adBreak.id, otherwise use ad.id
    let adImpressionId = '';
    if (
      args.eventState === 'adAllCompleted' &&
      (typeof adBreak?.id === 'string' || typeof adBreak?.id === 'number')
    ) {
      adImpressionId = String(adBreak.id);
    } else if (typeof ad?.id === 'string' || typeof ad?.id === 'number') {
      adImpressionId = String(ad.id);
    }

    const skipOffset = evt?.skipOffset;
    const adSkippable =
      typeof skipOffset === 'number' && !isNaN(skipOffset) ? '1' : '0';

    return {
      eventType: 'Ad',
      eventState: args.eventState,
      adImpressionId,
      playerSource: 'TvOS',
      domain: config.domain ?? '',
      path: config.path ?? '',
      adProfileId: config.adProfileId ?? '',
      playerProfileId: config.playerProfileId ?? '',
      adType: '1',
      adPosition,
      adDuration,
      adTagType: 'VAST',
      adTagUrl: '',
      adTitle: '',
      adPodPosition:
        typeof evt?.indexInQueue === 'number' ? String(evt.indexInQueue) : '0',
      adSkippable,
      adMidQuartile,
      adThirdQuartile,
      adTotalRequest: String(adRequestCount),
      completedAdsCount: String(completedAdsCount),
      shardId: config.shardId,
      muted: playerState.muted,
      autoplay: playerState.autoplay,
      ...(config.additionalMetadata ?? {}),
    };
  };

  // Helper to safely get property from event object
  const getEventProp = <T = unknown>(
    event: PlayerEventData,
    key: string
  ): T | undefined => {
    const eventRecord = event as Record<string, unknown>;
    return eventRecord[key] as T | undefined;
  };

  const handlePlayerEvent = async (event: PlayerEventData) => {
    const { name: eventName } = event;

    if (config.debug)
      console.log('[BitmovinRNAnalytics] Event:', eventName, event);

    // Reset quartile flags on each new ad start
    if (eventName === 'onAdStarted') {
      adMidQuartile = '0';
      adThirdQuartile = '0';
    }

    // Handle ad quartile events based on quartile property value
    if (eventName === 'onAdQuartile') {
      const quartile = getEventProp<string>(event, 'quartile');

      if (quartile === 'mid_point') {
        adMidQuartile = '1';
      } else if (quartile === 'third') {
        adThirdQuartile = '1';
      }
      return; // Don't send quartile as a separate ad event
    }

    // Reset counters on break start
    if (eventName === 'onAdBreakStarted') {
      completedAdsCount = 0;
      adRequestCount = 0;
      adMidQuartile = '0';
      adThirdQuartile = '0';
    } else if (eventName === 'onAdFinished') {
      completedAdsCount += 1;
    } else if (eventName === 'onAdStarted') {
      adRequestCount += 1;
    }

    const adState = AD_EVENT_STATE_MAP[eventName] ?? null;
    if (adState) {
      enqueueOrRun(async () => {
        const baseFields = await getBaseEventFields();
        eyevinnPlayerAnalytics?.metadata({
          event: 'metadata',
          ...baseFields,
          payload: await buildAdMetadataPayload({
            eventState: adState,
            event: event as Record<string, unknown>,
          }),
        } as Parameters<PlayerAnalytics['metadata']>[0]);

        // Reset after sending adAllCompleted
        if (eventName === 'onAdBreakFinished') {
          completedAdsCount = 0;
          adRequestCount = 0;
          adMidQuartile = '0';
          adThirdQuartile = '0';
        }
      });
      return;
    }

    const baseFields = await getBaseEventFields();

    switch (eventName) {
      case RNPlayerEvents.Playing:
        {
          enqueueOrRun(() => {
            const payload: PlayingArg = { event: 'playing', ...baseFields };
            eyevinnPlayerAnalytics?.playing(payload);
          });
        }
        break;

      case RNPlayerEvents.Paused:
        {
          enqueueOrRun(() => {
            const payload: PausedArg = { event: 'paused', ...baseFields };
            eyevinnPlayerAnalytics?.pause(payload);
          });
        }
        break;

      case RNPlayerEvents.Seek:
        {
          enqueueOrRun(() => {
            const payload: SeekingArg = { event: 'seeking', ...baseFields };
            eyevinnPlayerAnalytics?.seeking(payload);
          });
        }
        break;

      case RNPlayerEvents.Seeked:
        {
          enqueueOrRun(() => {
            const payload: SeekedArg = { event: 'seeked', ...baseFields };
            eyevinnPlayerAnalytics?.seeked(payload);
          });
        }
        break;

      case RNPlayerEvents.PlaybackFinished:
        // stopped with reason ended
        {
          enqueueOrRun(() => {
            const payload: StoppedArg = {
              event: 'stopped',
              ...baseFields,
              payload: { reason: 'ended' },
            };
            eyevinnPlayerAnalytics?.stopped(payload);
          });
        }
        break;

      case RNPlayerEvents.PlayerError:
      case RNPlayerEvents.SourceError:
        {
          enqueueOrRun(() => {
            const code =
              getEventProp<string | number>(event, 'code') ?? 'UNKNOWN';
            const message =
              getEventProp<string>(event, 'message') ?? 'Unknown error';
            const payload: ErrorArg = {
              event: 'error',
              ...baseFields,
              payload: {
                code,
                message,
              },
            };
            eyevinnPlayerAnalytics?.error(payload);
          });
        }
        break;

      case RNPlayerEvents.StallStarted:
        {
          enqueueOrRun(() => {
            const payload: BufferingArg = { event: 'buffering', ...baseFields };
            eyevinnPlayerAnalytics?.buffering(payload);
          });
        }
        break;

      case RNPlayerEvents.StallEnded:
        {
          enqueueOrRun(() => {
            const payload: BufferedArg = { event: 'buffered', ...baseFields };
            eyevinnPlayerAnalytics?.buffered(payload);
          });
        }
        break;

      case RNPlayerEvents.VideoPlaybackQualityChanged:
        enqueueOrRun(() => {
          const newVideoQuality = getEventProp<{ bitrate?: number }>(
            event,
            'newVideoQuality'
          );
          const bitrate = newVideoQuality?.bitrate ?? 0;

          eyevinnPlayerAnalytics?.bitrateChanged({
            event: 'bitrate_changed',
            ...baseFields,
            payload: {
              bitrate,
            },
          } as Parameters<PlayerAnalytics['bitrateChanged']>[0]);
        });
        break;

      default:
        break;
    }
  };

  const deinit = () => {
    if (config.debug) {
      console.info('[BitmovinAnalytics] deinit');
    }
    stopHeartbeat();
    eyevinnPlayerAnalytics?.destroy();
    eyevinnPlayerAnalytics = null;
    isInitiated = false;
    pendingEventQueue.length = 0;
  };

  const reportAdEvent = (payload: AdEventPayload): void => {
    // Map to single reporter as metadata event
    const { adState, event: adEvent } = payload;
    enqueueOrRun(async () => {
      const baseFields = await getBaseEventFields();
      eyevinnPlayerAnalytics?.metadata({
        event: 'metadata',
        ...baseFields,
        payload: await buildAdMetadataPayload({
          eventState: adState,
          event: adEvent,
        }),
      } as Parameters<PlayerAnalytics['metadata']>[0]);
    });
  };

  const reportMetadata = (metadata: Record<string, unknown>): void => {
    enqueueOrRun(async () => {
      const baseFields = await getBaseEventFields();
      eyevinnPlayerAnalytics?.metadata({
        event: 'metadata',
        ...baseFields,
        payload: metadata,
      } as Parameters<PlayerAnalytics['metadata']>[0]);
    });
  };

  return {
    init,
    deinit,
    handlePlayerEvent,
    reportAdEvent,
    reportMetadata,
    getAnalyticsInstance: () => eyevinnPlayerAnalytics,
  };
};
